import * as React from "react";
import { Genome } from "../../../api";
import { depthFormatter, percentageFormatter } from "../../../utils/metadata-formatter";
import { getGenomeStats } from "../../../utils/qc-statistics";
import { InfoTooltip } from "../../info-tooltip";
import { QualityControlSection, QualityControlWrapper, QualityStatus } from "../../quality-control";
import { MetadataCard, MetadataRow } from "../../metadata-card";
import { renderWithLinks } from "../../../utils/html";
import RNASeqQCSection from "../../rnaseq-qc-section";
import ExomeQCSection from "../../exome-qc-section";

class MicrobeDetailsQualityControl extends React.PureComponent<{
  genome: Genome;
  collectionName: string | null;
  notes: string | null;
}> {
  usesBuscoQC() {
    return !!this.props.collectionName && this.props.collectionName === "mycology";
  }

  usesCheckMQC() {
    return !!this.props.collectionName && this.props.collectionName === "bacteriology";
  }

  render() {
    return (
      <>
        <QualityControlWrapper>
          <IlluminaQualityStats
            genome={this.props.genome}
            isViral={this.props.collectionName === "virology"}
          />
          {this.usesCheckMQC() && <AssemblyQualityStatus genome={this.props.genome} />}
          {this.usesBuscoQC() && <FungalQualityStats genome={this.props.genome} />}
        </QualityControlWrapper>
        <QualityControlWrapper singleColumn={true}>
          {this.props.notes && (
            <QualityControlSection header="Notes">
              <p>{renderWithLinks(this.props.notes)}</p>
            </QualityControlSection>
          )}
          <ExomeQCSection genome={this.props.genome} />
          <RNASeqQCSection genome={this.props.genome} />
        </QualityControlWrapper>
      </>
    );
  }
}

const AssemblyQualityStatus = ({ genome }: { genome: Genome }) => {
  const genomeStats = getGenomeStats(genome);
  if (genomeStats?.qualityControl?.assembly === undefined) {
    return null;
  }

  const { assembly } = genomeStats.qualityControl;
  const { contamination, completeness, illuminaDepth, ontDepth } = assembly;

  const rows = (
    <>
      {completeness !== null && (
        <MetadataRow
          label="Estimated genome completeness"
          value={formatQCItemValue(assembly, "completeness", percentValueToString)}
        />
      )}
      {contamination !== null && (
        <MetadataRow
          label="Estimated genome contamination"
          value={formatQCItemValue(assembly, "contamination", percentValueToString)}
        />
      )}
      {illuminaDepth !== null && (
        <MetadataRow
          label="Illumina depth of coverage"
          value={formatQCItemValue(assembly, "illuminaDepth", depthFormatter)}
        />
      )}
      {ontDepth !== null && (
        <MetadataRow
          label="ONT depth of coverage"
          value={formatQCItemValue(assembly, "ontDepth", depthFormatter)}
        />
      )}
    </>
  );

  return (
    <QualityControlSection header="Assembly Quality Control">
      <MetadataCard
        title={
          <>
            Assembly Quality Control Metrics
            <InfoTooltip>
              CheckM assesses genome quality metrics including completeness and contamination. See
              Parks 2015
            </InfoTooltip>
          </>
        }
      >
        {rows}
      </MetadataCard>
    </QualityControlSection>
  );
};

const IlluminaQualityStats = ({ genome, isViral }: { genome: Genome; isViral: boolean }) => {
  const genomeStats = getGenomeStats(genome);
  if (genomeStats === null || genomeStats.qualityControl === undefined) {
    return null;
  }

  const { sequencing } = genomeStats.qualityControl;
  const { medianQScorePercent, ambiguousContent } = sequencing;

  const rows = (
    <>
      {!isViral && (
        <MetadataRow
          label="Number of trimmed reads"
          value={formatQCItemValue(sequencing, "trimmedReads")}
        />
      )}
      <MetadataRow
        label="Median Q score, all bases"
        value={formatQCItemValue(sequencing, "medianQScore")}
      />
      {medianQScorePercent !== null && (
        <MetadataRow
          label="Percent of median Q scores per cycle greater than 25"
          value={formatQCItemValue(sequencing, "medianQScorePercent", percentValueToString)}
        />
      )}
      {ambiguousContent !== null && (
        <MetadataRow
          label="Ambiguous content (% N bases)"
          value={formatQCItemValue(sequencing, "ambiguousContent", percentValueToString)}
        />
      )}
    </>
  );

  return (
    <QualityControlSection header="Sequencing Quality Control">
      <MetadataCard title="Sequencing Quality Control Metrics">{rows}</MetadataCard>
    </QualityControlSection>
  );
};

const FungalQualityStats = ({ genome }: { genome: Genome }) => {
  const genomeStats = getGenomeStats(genome);
  let depthMean: number | null = null;

  if (genomeStats?.sequencingStats?.illumina?.depth?.mean !== undefined) {
    depthMean = genomeStats.sequencingStats.illumina.depth.mean || 0;
  }
  const completeness = genomeStats?.qualityControl?.assembly?.completeness;

  const values = {
    depthMean,
    completeness,
  };

  if (!completeness) {
    return null;
  }

  const rows = (
    <>
      <MetadataRow
        label="Estimated genome completeness"
        value={formatQCItemValue(values, "completeness", percentValueToString)}
      />
      <MetadataRow
        label="Average depth of coverage"
        value={formatQCItemValue(values, "depthMean", depthFormatter)}
      />
    </>
  );

  return (
    <QualityControlSection header="BUSCO Quality Control">
      <MetadataCard title="BUSCO Quality Control Metrics">{rows}</MetadataCard>
    </QualityControlSection>
  );
};

// percentageFormatter expects a value to be a ratio [0, 1.0].
// This expects it to be a percent value [0.0, 100.0].
function percentValueToString(value: number): string {
  return percentageFormatter(value / 100.0);
}

export type OnlyNumberFields = Record<string, number | undefined | null>;

export function formatQCItemValue<T extends OnlyNumberFields>(
  obj: T | undefined,
  field: keyof T,
  valueFormatter?: (value: number) => string
): string {
  if (!obj) {
    return "-";
  }
  const value = obj[field];
  if (value === null || value === undefined) {
    return "-";
  }

  if (valueFormatter) {
    return valueFormatter(value);
  }
  return value.toLocaleString();
}

// NOTE - these cutoffs for PASSED, CAUTION, and FAILED are magic numbers based on conversations with Austin
export function getQCItemStatus<T extends OnlyNumberFields>(
  obj: T | undefined,
  field: keyof T,
  bounds: [number, number]
): QualityStatus {
  if (!obj) {
    return QualityStatus.Missing;
  }
  const value = obj[field];
  if (value === null || value === undefined) {
    return QualityStatus.Missing;
  }

  const [b0, b1] = bounds;
  const lowerIsBetter = b0 < b1;

  if (lowerIsBetter) {
    if (value <= b0) {
      return QualityStatus.Passed;
    }
    if (value <= b1) {
      return QualityStatus.Caution;
    }
    return QualityStatus.Failed;
  }

  if (value >= b0) {
    return QualityStatus.Passed;
  }
  if (value >= b1) {
    return QualityStatus.Caution;
  }

  return QualityStatus.Failed;
}

export default MicrobeDetailsQualityControl;
